/**
 * Client ↔ rago-web sharing DTOs (ARCHITECTURE §4a) — the zod validators
 * behind the /v1/connections + /v1/users/lookup slice of
 * contracts/openapi/web-broker.v1.yaml. Sharing is a CONNECTION between two
 * accounts (Plex's friends): requested by one, accepted once by the other;
 * from then on either side shares libraries from the servers they own to the
 * other without another acceptance, and either side can break it, which
 * revokes both directions. Invites target existing accounts only (v1).
 */
import { z } from "zod";

const metaSchema = z.object({
  offset: z.number().int().min(0),
  limit: z.number().int().min(0),
  total: z.number().int().min(0),
});

/**
 * `"*"` = every library, including ones created later. An explicit list is
 * frozen at grant time; the cloud never validates ids against the server's
 * real libraries (it must not know them — TV §7), unknown ids just slice to
 * nothing server-side.
 */
export const shareLibraryIdsSchema = z.union([
  z.literal("*"),
  z.array(z.number().int().min(0)).min(1).max(500),
]);
export type ShareLibraryIds = z.infer<typeof shareLibraryIdsSchema>;

/**
 * The share's reserved `restrictions` object as a client SETS it (Plex's
 * Manage Library Access › Restrictions): every key optional, so a request
 * without it leaves what is stored alone, and one that carries it replaces
 * the stored object whole. The media server enforces them; an owner is
 * never restricted. `allowDownloads` — save files and download copies from
 * the shared libraries — reads as true when absent.
 */
export const shareRestrictionsSchema = z.object({ allowDownloads: z.boolean() }).partial();
export type ShareRestrictions = z.infer<typeof shareRestrictionsSchema>;

export const userLookupResponseSchema = z.object({
  userId: z.uuid(),
  username: z.string(),
  displayName: z.string(),
});
export type UserLookupResponse = z.infer<typeof userLookupResponseSchema>;

export const connectionStatusSchema = z.enum(["pending", "accepted"]);
export type ConnectionStatus = z.infer<typeof connectionStatusSchema>;

/** Relative to the caller: `sent` = I asked, `received` = they asked. */
export const connectionDirectionSchema = z.enum(["sent", "received"]);
export type ConnectionDirection = z.infer<typeof connectionDirectionSchema>;

/** One server's libraries shared across a connection, in one direction. */
export const shareEntrySchema = z.object({
  shareId: z.uuid(),
  serverId: z.uuid(),
  serverName: z.string(),
  libraryIds: shareLibraryIdsSchema,
  /** Always present on the way out: absent or malformed storage reads as
   * allowed (lib/sharing.ts normalizeRestrictions). */
  restrictions: z.object({ allowDownloads: z.boolean() }),
  updatedAt: z.iso.datetime(),
});
export type ShareEntry = z.infer<typeof shareEntrySchema>;

/** A connection as the caller sees it: the OTHER person, what I share with
 * them (`given`, my servers) and what they share with me (`received`). A
 * server with no entry is not shared ("No library access"). */
export const connectionSchema = z.object({
  id: z.uuid(),
  status: connectionStatusSchema,
  direction: connectionDirectionSchema,
  user: userLookupResponseSchema,
  createdAt: z.iso.datetime(),
  respondedAt: z.iso.datetime().nullable(),
  given: z.array(shareEntrySchema),
  received: z.array(shareEntrySchema),
});
export type Connection = z.infer<typeof connectionSchema>;

export const connectionsResponseSchema = z.object({
  meta: metaSchema,
  items: z.array(connectionSchema),
});
export type ConnectionsResponse = z.infer<typeof connectionsResponseSchema>;

export const connectionShareRequestSchema = z.object({
  serverId: z.uuid(),
  libraryIds: shareLibraryIdsSchema,
  restrictions: shareRestrictionsSchema.optional(),
});
export type ConnectionShareRequest = z.infer<typeof connectionShareRequestSchema>;

/** Invite an account: ONE connection, with what each of my servers shares
 * from the start (may be empty — a bare request). */
export const createConnectionRequestSchema = z
  .object({
    userId: z.uuid(),
    shares: z.array(connectionShareRequestSchema).max(50).default([]),
  })
  .refine((v) => new Set(v.shares.map((s) => s.serverId)).size === v.shares.length, {
    message: "Each server at most once",
    path: ["shares"],
  });
export type CreateConnectionRequest = z.infer<typeof createConnectionRequestSchema>;

export const setConnectionShareRequestSchema = z.object({
  libraryIds: shareLibraryIdsSchema,
  /** Absent = unchanged; present = replaced whole. */
  restrictions: shareRestrictionsSchema.optional(),
});
export type SetConnectionShareRequest = z.infer<typeof setConnectionShareRequestSchema>;

/** The 409 of POST /v1/connections: the pair already has a connection. Nested
 * under `connection` because a problem's own `status` is the HTTP code. */
export const connectionConflictSchema = z.object({
  connection: z.object({
    id: z.uuid(),
    status: connectionStatusSchema,
    direction: connectionDirectionSchema,
  }),
});
export type ConnectionConflict = z.infer<typeof connectionConflictSchema>;
